import { A11yModule } from '@angular/cdk/a11y';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { ARCHIVE_FOLDER_NAME, IFolder, ISidebarFolder } from '../../data-rules-model';
import { appearFromAbove } from '../../rule/kyloe-animations';
import { FolderAutocompleteComponent } from '../folder-autocomplete/folder-autocomplete.component';
import { RuleSearchAutocompleteComponent } from '../rule-search-autocomplete/rule-search-autocomplete.component';

export interface IPopupOption {
  id: string;
  name: string;
}

export interface IFolderOption extends IPopupOption {
  icon: string;
  colour?: string;
  outlinedIcon?: boolean;
}

export interface IRuleList extends IPopupOption {
  entity: string;
  iconClass: string;
}

export type FolderPopupMode = 'manage-rules' | 'manage-folders' | 'create-folder';

@Component({
  selector: 'app-folder-popup',
  standalone: true,
  templateUrl: './folder-popup.component.html',
  styleUrls: ['./folder-popup.component.scss'],
  imports: [
    NgIf,
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    MatTooltip,
    A11yModule,
    ReactiveFormsModule,
    RuleSearchAutocompleteComponent,
    FolderAutocompleteComponent
  ],
  animations: [appearFromAbove]
})
export class FolderPopupComponent implements OnChanges, AfterViewInit {
  @ViewChild(FolderAutocompleteComponent) private folderAutocomplete: FolderAutocompleteComponent;
  @ViewChild('renameInput') private renameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('createFolderInput') private createFolderInput?: ElementRef<HTMLInputElement>;
  /**
   * Controls which view the popup renders.
   * - `manage-rules`: popup is attached to a folder; user manages which rules belong to it.
   * - `manage-folders`: popup is attached to a rule; user manages which folders it belongs to.
   */
  @Input() mode: FolderPopupMode = 'manage-rules';

  @Input() folder: ISidebarFolder;
  @Input() allRules: IRuleList[] = [];
  @Input() folderRules: IRuleList[] = [];

  @Input() ruleFolders: IFolder[] = [];
  @Input() allFolders: ISidebarFolder[] = [];
  @Input() reservedFolderNames: string[] = [];

  isRenaming = false;
  renameValue = '';
  createFolderControl = new FormControl('');
  folderNameAlreadyExists = false;

  private duplicateCheckDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ruleFolderOptions: IFolderOption[] = [];
  availableFolders: IFolderOption[] = [];
  folderRuleIds: string[] = [];
  showArchiveInSearch = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allFolders'] || changes['ruleFolders']) {
      const currentIds = new Set(this.ruleFolders.map(f => f.id));
      this.ruleFolderOptions = this.ruleFolders.map(f => this.toFolderOption(f));
      this.availableFolders = this.allFolders
        .filter(f => f.id !== 'all' && !currentIds.has(f.id))
        .map(f => ({ id: f.id, name: f.name, icon: f.icon || 'folder', colour: f.colour, outlinedIcon: true }));
      this.showArchiveInSearch = !currentIds.has('archive');
      this.allFolderNames = this.allFolders.filter(f => f.id !== 'all').map(f => f.name);
    }
    if (changes['folderRules']) {
      this.folderRuleIds = (this.folderRules ?? []).map(r => r.id);
    }
  }

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly renameRequested = new EventEmitter<string>();
  @Output() readonly deleteRequested = new EventEmitter<void>();
  @Output() readonly ruleAdded = new EventEmitter<IRuleList>();
  @Output() readonly ruleRemoved = new EventEmitter<IRuleList>();
  @Output() readonly folderAdded = new EventEmitter<IFolder>();
  @Output() readonly folderRemoved = new EventEmitter<IFolder>();
  @Output() readonly folderCreated = new EventEmitter<string>();
  @Output() readonly archiveRequested = new EventEmitter<void>();

  allFolderNames: string[] = [];

  enableRenaming(): void {
    this.renameValue = this.folder?.name ?? '';
    this.folderNameAlreadyExists = false;
    this.isRenaming = true;
    setTimeout(() => this.renameInput?.nativeElement.focus());
  }

  onRenameInput(value: string): void {
    const trimmed = value.trim();
    if (trimmed.toLowerCase() !== this.renameValue.trim().toLowerCase()) {
      this.folderNameAlreadyExists = false;
    }
    this.renameValue = value;
    const currentName = this.folder?.name?.trim().toLowerCase() ?? '';
    this.debounceDuplicateCheck(trimmed.toLowerCase() !== currentName ? trimmed : '', this.folder?.id);
  }

  submitRename(event?: Event): void {
    event?.preventDefault();
    const trimmed = this.renameValue.trim();
    if (!trimmed || trimmed === this.folder?.name) {
      this.isRenaming = false;
      return;
    }
    // Covers off if the user is quick enough to press enter in under 300ms
    this.checkFolderNameDuplicate(trimmed, this.folder?.id);
    if (this.folderNameAlreadyExists) {
      return;
    }
    this.renameRequested.emit(trimmed);
    this.isRenaming = false;
  }

  cancelRename(event?: Event): void {
    event?.stopPropagation();
    this.debounceDuplicateCheck('');
    this.folderNameAlreadyExists = false;
    this.isRenaming = false;
  }

  ngAfterViewInit(): void {
    if (this.mode === 'create-folder') {
      setTimeout(() => this.createFolderInput?.nativeElement.focus());
    }
  }

  onCreateFolderInput(): void {
    const trimmed = (this.createFolderControl.value ?? '').trim();
    this.folderNameAlreadyExists = false;
    this.debounceDuplicateCheck(trimmed);
  }

  private debounceDuplicateCheck(value: string, excludeId?: string): void {
    if (this.duplicateCheckDebounce !== null) {
      clearTimeout(this.duplicateCheckDebounce);
      this.duplicateCheckDebounce = null;
    }
    if (value) {
      this.duplicateCheckDebounce = setTimeout(() => this.checkFolderNameDuplicate(value, excludeId), 300);
    }
  }

  private checkFolderNameDuplicate(name: string, excludeId?: string): void {
    const nameLowerCase = name.toLowerCase();
    if (!nameLowerCase) {
      this.folderNameAlreadyExists = false;
      return;
    }
    const reservedSet = new Set(this.reservedFolderNames.map(n => n.toLowerCase()));
    this.folderNameAlreadyExists = reservedSet.has(nameLowerCase)
      || this.allFolders.some(f => f.id !== excludeId && f.name.trim().toLowerCase() === nameLowerCase);
  }

  submitCreateFolder(event?: Event): void {
    event?.preventDefault();
    const trimmed = (this.createFolderControl.value ?? '').trim();
    if (!trimmed) {
      return;
    }
    // Covers off if the user is quick enough to press enter in under 300ms
    this.checkFolderNameDuplicate(trimmed);
    if (this.folderNameAlreadyExists) {
      return;
    }
    this.folderCreated.emit(trimmed);
    this.close();
  }

  cancelCreate(event?: Event): void {
    event?.stopPropagation();
    if (this.createFolderControl.value) {
      this.createFolderControl.setValue('');
      this.folderNameAlreadyExists = false;
    } else {
      this.close();
    }
  }

  @HostListener('document:change', ['$event'])
  onDocumentChange(event: Event): void {
    if ((event.target as HTMLElement)?.id === 'hamburger') {
      this.close();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.mode !== 'manage-folders') {
      return;
    }
    const path = event.composedPath();
    const insidePopup = path.includes(this.elementRef.nativeElement);
    const insideOverlayPane = path.some(node => node instanceof Element && node.classList.contains('cdk-overlay-pane'));
    if (!insidePopup && !insideOverlayPane) {
      this.close();
    }
  }

  close(): void {
    this.isRenaming = false;
    this.closed.emit();
  }

  onRuleSearchSelected(rule: IRuleList): void {
    this.ruleAdded.emit(rule);
  }

  removeRule(rule: IRuleList, event: MouseEvent): void {
    event.stopPropagation();
    this.ruleRemoved.emit(rule);
  }

  onFolderSearchSelected(option: IFolderOption): void {
    this.folderAdded.emit({ id: option.id, name: option.name });
  }

  onFolderSearchChanged(value: string): void {
    const nameLowerCase = value.trim().toLowerCase();
    const archiveNameLowerCase = ARCHIVE_FOLDER_NAME.toLowerCase();
    this.folderNameAlreadyExists = !!nameLowerCase && this.reservedFolderNames
      .some(n => n.trim().toLowerCase() !== archiveNameLowerCase && n.trim().toLowerCase() === nameLowerCase);
  }

  removeFolder(option: IFolderOption, event: MouseEvent): void {
    event.stopPropagation();
    this.folderAutocomplete?.closePanel();
    this.folderRemoved.emit({ id: option.id, name: option.name });
  }

  private toFolderOption(folder: IFolder): IFolderOption {
    if (folder.id === 'archive') {
      return { id: folder.id, name: folder.name, icon: 'inventory_2', outlinedIcon: true };
    }
    const sidebarFolder = this.allFolders.find(f => f.id === folder.id);
    return {
      id: folder.id,
      name: folder.name,
      icon: sidebarFolder?.icon || 'folder',
      colour: sidebarFolder?.colour,
      outlinedIcon: true
    };
  }

}
